import React, { useState, useEffect } from "react";
import { ref, onValue, push, update, remove } from "firebase/database";
import { db } from "./firebase";
import { formatCourseId } from "./utils/formatCourse";

const AdminMeetings = () => {
  // State declarations
  const [meetings, setMeetings] = useState([]);
  const [courses, setCourses] = useState([]);
  const [activeCourse, setActiveCourse] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({
    title: "",
    link: "",
    meetingTime: "",
    description: ""
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddMeetingExpanded, setIsAddMeetingExpanded] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  // Fetch courses from Firebase
  useEffect(() => {
    const coursesRef = ref(db, "courses");
    onValue(coursesRef, (snapshot) => {
      if (snapshot.exists()) {
        const coursesData = Object.keys(snapshot.val());
        setCourses(coursesData);
      }
    });
  }, []);

  // Fetch meetings for active course from courses/{courseId}/meetings
  useEffect(() => {
    if (!activeCourse) return;

    const meetingsRef = ref(db, `courses/${activeCourse}/meetings`);
    onValue(meetingsRef, (snapshot) => {
      if (snapshot.exists()) {
        const meetingsData = Object.entries(snapshot.val()).map(([id, data]) => ({
          id,
          ...data,
          meetingTime: data.meetingTime || ""
        }));
        setMeetings(meetingsData);
      } else {
        setMeetings([]);
      }
    });
  }, [activeCourse]);

  // Handle form changes
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Start editing a meeting
  const startEditing = (meeting) => {
    setEditingId(meeting.id);
    setEditData({
      title: meeting.title,
      link: meeting.link,
      meetingTime: meeting.meetingTime,
      description: meeting.description || ""
    });
    setIsAddMeetingExpanded(true);
  };

  // Save meeting data
  const saveEdit = () => {
    if (!editingId || !editData.title || !editData.link) {
      alert("Title and Link are required");
      return;
    }

    const meetingData = {
      ...editData,
      updatedAt: new Date().toISOString()
    };

    update(ref(db, `courses/${activeCourse}/meetings/${editingId}`), meetingData)
      .then(() => {
        alert("Meeting updated successfully");
        resetForm();
      })
      .catch(error => alert("Error updating meeting: " + error.message));
  };

  // Add new meeting
  const addNewMeeting = () => {
    if (!editData.title || !editData.link) {
      alert("Title and Link are required");
      return;
    }

    const meetingData = {
      ...editData,
      createdAt: new Date().toISOString()
    };

    push(ref(db, `courses/${activeCourse}/meetings`), meetingData)
      .then(() => {
        alert("Meeting added successfully");
        resetForm();
      })
      .catch(error => alert("Error adding meeting: " + error.message));
  };

  // Delete meeting
  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this meeting?")) {
      remove(ref(db, `courses/${activeCourse}/meetings/${id}`))
        .catch(error => alert("Error deleting meeting: " + error.message));
    }
  };

  // Reset form
  const resetForm = () => {
    setEditData({
      title: "",
      link: "",
      meetingTime: "",
      description: ""
    });
    setEditingId(null);
    setIsAddMeetingExpanded(false);
  };

  // Handle sorting
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Sorting and filtering
  const sortedMeetings = React.useMemo(() => {
    let sortableMeetings = [...meetings];
    if (sortConfig.key) {
      sortableMeetings.sort((a, b) => {
        // Special handling for dates
        if (sortConfig.key === 'meetingTime') {
          const aValue = a.meetingTime ? new Date(a.meetingTime).getTime() : 0;
          const bValue = b.meetingTime ? new Date(b.meetingTime).getTime() : 0;
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }

        // Default sorting for other fields
        const aValue = a[sortConfig.key] || "";
        const bValue = b[sortConfig.key] || "";
        if (sortConfig.direction === "asc") {
          return aValue.toString().localeCompare(bValue.toString());
        } else {
          return bValue.toString().localeCompare(aValue.toString());
        }
      });
    }
    return sortableMeetings.filter(meeting =>
      meeting.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (meeting.description && meeting.description.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  }, [meetings, sortConfig, searchQuery]);

  return (
    <div className="p-4 text-black min-h-screen">
      <h1 className="text-3xl font-bold mb-6 text-black">OnlineMeetings</h1>

      {/* Course selector */}
      <div className="mb-6">
        <label htmlFor="meetings-course" className="block text-sm font-medium text-gray-700 mb-1">Course</label>
        <select
          id="meetings-course"
          value={activeCourse}
          onChange={(e) => setActiveCourse(e.target.value)}
          className="p-2 border rounded-md text-gray-900 min-w-[200px]"
        >
          <option value="">Select course</option>
          {courses.map((course) => (
            <option key={course} value={course}>{formatCourseId(course)}</option>
          ))}
        </select>
      </div>

      {!activeCourse ? (
        <div className="flex flex-col items-center justify-center py-16 px-6 bg-gradient-to-b from-gray-50 to-white border-2 border-dashed border-gray-200 rounded-2xl shadow-sm">
          <div className="w-16 h-16 mb-4 rounded-full bg-blue-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-1">No course selected</h3>
          <p className="text-gray-500 text-sm max-w-sm">Please select a course from the dropdown above to view and manage content.</p>
        </div>
      ) : (
        <>
      {/* Search and Add Meeting */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <input
          type="text"
          placeholder="Search meetings..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="p-2 border rounded-md"
        />
        <button
          onClick={() => setIsAddMeetingExpanded(!isAddMeetingExpanded)}
          className="bg-green-600 text-white p-2 rounded-md hover:bg-green-700"
        >
          {isAddMeetingExpanded ? "Cancel" : "Add New Meeting"}
        </button>
      </div>

      {/* Add/Edit Meeting Form */}
      {isAddMeetingExpanded && (
        <div className="bg-white p-4 rounded-lg shadow-md mb-6">
          <h2 className="text-xl font-semibold mb-4">
            {editingId ? "Edit Meeting" : "Add New Meeting"}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Title*</label>
              <input
                type="text"
                name="title"
                value={editData.title}
                onChange={handleEditChange}
                className="w-full p-2 border rounded-md"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Zoom Link*</label>
              <input
                type="url"
                name="link"
                value={editData.link}
                onChange={handleEditChange}
                className="w-full p-2 border rounded-md"
                required
                placeholder="https://zoom.us/j/..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Meeting Time*</label>
              <input
                type="datetime-local"
                name="meetingTime"
                value={editData.meetingTime}
                onChange={handleEditChange}
                className="w-full p-2 border rounded-md"
                required
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                name="description"
                value={editData.description}
                onChange={handleEditChange}
                className="w-full p-2 border rounded-md"
                rows="3"
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end space-x-2">
            <button
              onClick={resetForm}
              className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              onClick={editingId ? saveEdit : addNewMeeting}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
            >
              {editingId ? "Update" : "Add"} Meeting
            </button>
          </div>
        </div>
      )}

      {/* Meetings Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-300 text-black">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort("title")}
                >
                  Title {sortConfig.key === "title" && (sortConfig.direction === "asc" ? "↑" : "↓")}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort("meetingTime")}
                >
                  Date/Time {sortConfig.key === "meetingTime" && (sortConfig.direction === "asc" ? "↑" : "↓")}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                  Link
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedMeetings.map(meeting => (
                <tr key={meeting.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium">{meeting.title}</div>
                    {meeting.description && (
                      <div className="text-sm text-gray-500">{meeting.description}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {meeting.meetingTime ? new Date(meeting.meetingTime).toLocaleString() : "-"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <a
                      href={meeting.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      Join Meeting
                    </a>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap space-x-2">
                    <button
                      onClick={() => startEditing(meeting)}
                      className="bg-blue-600 text-white px-3 py-1 rounded text-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(meeting.id)}
                      className="bg-red-600 text-white px-3 py-1 rounded text-sm"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {sortedMeetings.length === 0 && (
          <div className="p-4 text-center text-gray-500">
            No meetings found {searchQuery ? "matching your search" : "for this course"}
          </div>
        )}
      </div>
        </>
      )}
    </div>
  );
};

export default AdminMeetings;